import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Plus, Trash2, Save, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ApproverRow = ({ a, onRemove }) => (
  <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`po-approver-${a.email}`}>
    <div>
      <p className="text-sm font-medium text-slate-800">{a.name || a.email}</p>
      <p className="text-xs text-slate-500">{a.email}</p>
      {a.account_found === false && (
        <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3 w-3" />No user account with this email yet</p>
      )}
    </div>
    <Button variant="ghost" size="sm" onClick={onRemove} data-testid={`remove-po-approver-${a.email}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
  </div>
);

export default function POApproversSettings() {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/purchase-orders/approvers`, auth()).then((r) => setList(r.data.approvers)).catch(() => toast.error('Could not load approvers'));
  useEffect(() => { load(); }, []);

  const add = () => {
    const e = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(e)) return toast.error('Enter a valid email');
    if (!list.some((a) => a.email === e)) setList([...list, { email: e }]);
    setEmail('');
  };
  const save = async () => {
    if (!list.length) return toast.error('Keep at least one approver');
    setSaving(true);
    try {
      await axios.put(`${API}/purchase-orders/approvers`, { emails: list.map((a) => a.email) }, auth());
      toast.success('PO approvers updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="po-approvers-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="h-5 w-5 text-blue-600" />PO Approvers</CardTitle>
        <p className="text-sm text-slate-500">Only these users can approve or reject purchase orders (single and bulk). Everyone else sees the approval queue as view-only.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.map((a) => <ApproverRow key={a.email} a={a} onRemove={() => setList(list.filter((x) => x.email !== a.email))} />)}
        <div className="flex gap-2">
          <Input placeholder="approver@oltowns.in" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="po-approver-email-input" />
          <Button variant="outline" onClick={add} data-testid="add-po-approver-btn"><Plus className="mr-1 h-4 w-4" />Add</Button>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-po-approvers-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Approvers
        </Button>
      </CardContent>
    </Card>
  );
}
